/**
 * MindMapSkeleton
 *
 * A skeleton loading state for the MindMap component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface MindMapSkeletonProps {
  className?: string
}

export const MindMapSkeleton: React.FC<MindMapSkeletonProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Mind map visualization */}
      <div className="p-4">
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
          <div className="relative flex size-full items-center justify-center">
            {/* Central node */}
            <div className="absolute flex h-12 w-36 items-center justify-center rounded-lg bg-blue-200 dark:bg-blue-900">
              <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>

            {/* Connected nodes */}
            <div className="absolute left-1/3 top-1/4 h-10 w-28 rounded-lg bg-green-200 dark:bg-green-900"></div>
            <div className="absolute right-1/3 top-1/4 h-10 w-28 rounded-lg bg-purple-200 dark:bg-purple-900"></div>
            <div className="absolute bottom-1/4 left-1/3 h-10 w-28 rounded-lg bg-yellow-200 dark:bg-yellow-900"></div>
            <div className="absolute bottom-1/4 right-1/3 h-10 w-28 rounded-lg bg-red-200 dark:bg-red-900"></div>

            {/* Connection lines */}
            <svg className="absolute inset-0 size-full" xmlns="http://www.w3.org/2000/svg">
              <line
                x1="50%"
                y1="50%"
                x2="33%"
                y2="25%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
              <line
                x1="50%"
                y1="50%"
                x2="67%"
                y2="25%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
              <line
                x1="50%"
                y1="50%"
                x2="33%"
                y2="75%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
              <line
                x1="50%"
                y1="50%"
                x2="67%"
                y2="75%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="border-t p-3">
        <div className="flex justify-end gap-2">
          <div className="size-8 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="size-8 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="size-8 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    </div>
  )
}
